import {
  InputHTMLAttributes,
  useCallback,
  useId,
  useRef,
} from 'react';
import formStyles from '~/styles/form.module.css';
import FormLabel from './FormLabel';

type Props = {
  label: string;
  fieldType?: string;
  value: string | number;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  min?: number;
  max?: number;
  affix?: string;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  visuallyHiddenLabel?: boolean;
  selectOnFocus?: boolean;
  readOnly?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

export default function PlainTextField({
  label,
  fieldType = 'text',
  rows,
  value,
  placeholder,
  autoComplete,
  min,
  max,
  affix,
  required = false,
  visuallyHiddenLabel = false,
  selectOnFocus = false,
  readOnly = false,
  onChange,
  onBlur,
  onFocus,
  onKeyUp,
}: Props) {
  const fieldId = useId();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const setRef = useCallback((node: HTMLInputElement | HTMLTextAreaElement | null) => {
    inputRef.current = node;
  }, []);

  const handleFocus = useCallback((
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (selectOnFocus) {
      inputRef.current?.select();
    }

    onFocus?.(event);
  }, [selectOnFocus, onFocus, inputRef]);

  const handleBlur = useCallback((
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let newValue = event.target.value;
    const oldValue = newValue;

    // If its a number field
    if (fieldType === 'number') {
      if (min && Number(newValue) < min) {
        newValue = min.toString();
      }

      if (max && Number(newValue) > max) {
        newValue = max.toString();
      }
    }

    if (newValue !== oldValue) {
      const newEvent = { ...event, target: { ...event.target, value: newValue } };
      onChange(newEvent);
    }

    onBlur?.(event);
  }, [onBlur, fieldType, min, max, onChange]);

  const handleChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChange(event);
  }, [onChange]);

  const handleKeyUp = useCallback((
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onKeyUp?.(event);
  }, [onKeyUp]);

  const maxLength = fieldType !== 'number' ? max : undefined;

  return (
    <div>
      <FormLabel
        htmlFor={fieldId}
        required={required}
        visuallyHidden={visuallyHiddenLabel}
      >
        {label}
      </FormLabel>

      {
        rows
          ? (
            <textarea
              id={fieldId}
              ref={setRef}
              className={formStyles.formControl}
              value={value}
              onChange={handleChange}
              onKeyUp={handleKeyUp}
              onBlur={handleBlur}
              onFocus={handleFocus}
              rows={rows}
              placeholder={placeholder}
              maxLength={maxLength}
              required={required}
              autoComplete={autoComplete}
              readOnly={readOnly}
            />
          )
          : (
            <div className={formStyles.formControlWrapper}>
              <input
                id={fieldId}
                ref={setRef}
                type={fieldType}
                className={formStyles.formControl}
                value={value}
                placeholder={placeholder}
                min={fieldType === 'number' ? min : undefined}
                max={fieldType === 'number' ? max : undefined}
                maxLength={maxLength}
                onChange={handleChange}
                onKeyUp={handleKeyUp}
                onBlur={handleBlur}
                onFocus={handleFocus}
                required={required}
                autoComplete={autoComplete}
                readOnly={readOnly}
              />

              {affix && (
                <div className={formStyles.formControlAffix}>
                  {affix}
                </div>
              )}
            </div>
          )
      }
    </div>
  );
}
